import { useContext, useEffect, useMemo, useState } from "react";
import { AuthContext } from "../../context/Auth/AuthContext";
import { WhatsAppsContext } from "../../context/WhatsApp/WhatsAppsContext";
import { completaFilasDasConexoes } from "../../components/PromptModal/vinculoRules";
import { temAgenteNoTicket, iaAtivaNoTicket } from "./ticketAIRules";
import {
  carregaDados,
  dadosEmCache,
  invalidaCache,
  ouvintes,
} from "./dadosAgentes";

// Uma assinatura por (socket, empresa), por mais hooks montados (TicketInfo
// e os botões do ticket montam juntos): senão cada evento invalidaria N vezes.
let assinatura = null;

const assinaEventos = (socket, companyId) => {
  if (!socket || typeof socket.on !== "function" || !companyId) return undefined;
  if (
    !assinatura ||
    assinatura.socket !== socket ||
    assinatura.companyId !== companyId
  ) {
    if (assinatura) assinatura.desliga();
    const eventos = [`company-${companyId}-prompt`, `company-${companyId}-queue`];
    eventos.forEach((e) => socket.on(e, invalidaCache));
    assinatura = {
      socket,
      companyId,
      usos: 0,
      desliga: () => eventos.forEach((e) => socket.off(e, invalidaCache)),
    };
  }
  const atual = assinatura;
  atual.usos += 1;
  return () => {
    atual.usos -= 1;
    if (atual.usos === 0 && assinatura === atual) {
      atual.desliga();
      assinatura = null;
    }
  };
};

const useTicketAI = (ticket) => {
  const { user, socket } = useContext(AuthContext) || {};
  const { whatsApps, loading: carregandoConexoes } =
    useContext(WhatsAppsContext) || {};
  const companyId = user?.companyId;
  const [dados, setDados] = useState(
    () => dadosEmCache(companyId) || { prompts: [], filas: [] }
  );
  // Sem isso o consumidor não distingue "não tem agente" de "ainda não sei":
  // na primeira montagem da sessão o cache está vazio e `temAgente` nascia
  // false, escondendo o botão até o GET resolver.
  const [carregando, setCarregando] = useState(
    () => dadosEmCache(companyId) === null
  );

  useEffect(() => {
    let ativo = true;
    // Trocou de empresa com o hook montado: não mostra agentes da anterior
    // enquanto a lista nova chega.
    if (!dadosEmCache(companyId)) {
      setDados({ prompts: [], filas: [] });
      setCarregando(true);
    }
    const recarrega = () =>
      carregaDados(companyId).then((novos) => {
        if (!ativo) return;
        setDados(novos);
        setCarregando(false);
      });
    ouvintes.add(recarrega);
    recarrega();
    return () => {
      ativo = false;
      ouvintes.delete(recarrega);
    };
  }, [companyId]);

  useEffect(() => assinaEventos(socket, companyId), [socket, companyId]);

  const whatsapp = useMemo(() => {
    if (!ticket || !Array.isArray(whatsApps)) return undefined;
    const conexao = whatsApps.find((w) => Number(w.id) === Number(ticket.whatsappId));
    return conexao ? completaFilasDasConexoes([conexao], dados.filas)[0] : undefined;
  }, [whatsApps, ticket, dados.filas]);

  const temAgente = useMemo(
    () =>
      temAgenteNoTicket({
        ticket,
        prompts: dados.prompts,
        whatsapp,
        filas: dados.filas,
      }),
    [ticket, dados, whatsapp]
  );

  const iaAtiva = iaAtivaNoTicket({ ticket, temAgente });

  // Ticket sem fila depende da conexão (menu, "antes da distribuição").
  const esperandoConexao = Boolean(ticket) && !whatsapp && Boolean(carregandoConexoes);

  return { temAgente, iaAtiva, carregando: carregando || esperandoConexao };
};

export default useTicketAI;
